import { useMemo } from 'react';

import { parentalControlsHelper } from '../../helpers/parentalControls';

import { ActivateParentalControls } from './States/ActivateParentalControls/ActivateParentalControls';
import { Confirmation } from './States/Confirmation/Confirmation';
import { ConfirmCode } from './States/ConfirmCode/ConfirmCode';
import { DisableParentalControls } from './States/DisableParentalControls/DisableParentalControls';
import { DisabledParentalControls } from './States/DisabledParentalControls/DisabledParentalControls';
import { useParentalControls } from './shared/useParentalControls';

import type { FC } from 'react';

import * as styles from './ParentalControls.module.scss';

interface ParentalControlsViewProps {
  activationCode?: string;
  state: string;
  handleDisable: () => void;
  handleClose: () => void;
  handleLock: (pinCode?: string, ageLimit?: number) => void;
  handleValidate: () => void;
  handleUnlock: () => void;
}

const ParentalControlsView: FC<ParentalControlsViewProps> = ({
  state,
  activationCode,
  handleDisable,
  handleClose,
  handleLock,
  handleValidate,
  handleUnlock,
}) => {
  const { parentalControlsManagerVisible } = useParentalControls();
  const childComponent = useMemo(() => {
    if (!parentalControlsManagerVisible) {
      return null;
    }

    if (
      state !== 'code-confirmed' &&
      parentalControlsHelper.isParentalLockSet()
    ) {
      const code = parentalControlsHelper.getPin();
      return (
        <DisableParentalControls
          code={code}
          handleDisable={handleDisable}
          handleClose={handleClose}
        />
      );
    }

    switch (state) {
      case 'initial':
        return (
          <ActivateParentalControls
            handleLock={handleLock}
            handleClose={handleClose}
          />
        );
      case 'code-set':
        return (
          <ConfirmCode
            code={activationCode}
            handleValidate={handleValidate}
            handleClose={handleClose}
          />
        );
      case 'code-confirmed':
        return (
          <Confirmation handleClose={handleClose} handleUnlock={handleUnlock} />
        );
      case 'code-remove':
        return (
          <DisableParentalControls
            code={activationCode}
            handleDisable={handleDisable}
            handleClose={handleClose}
          />
        );
      case 'code-removed':
        return <DisabledParentalControls handleClose={handleClose} />;
    }
  }, [
    activationCode,
    parentalControlsManagerVisible,
    state,
    handleValidate,
    handleUnlock,
    handleClose,
    handleLock,
    handleDisable,
  ]);

  if (!childComponent) {
    return null;
  }

  return <div className={styles.container}>{childComponent}</div>;
};

export { ParentalControlsView };
